import React from 'react';
import { Link } from 'react-router-dom';
import { UsersIcon, BookOpenIcon, MapPinIcon, GlobeIcon, CheckIcon, ArrowRightIcon, LayersIcon, LinkIcon, SearchIcon, FileTextIcon } from 'lucide-react';
import { Badge } from '../components/ui/Badge';
export function FeaturesPage() {
  return <main className="w-full bg-cream" data-id="element-330">
      {/* Hero */}
      <section className="pt-32 pb-20 px-6 lg:px-8 bg-cream" aria-labelledby="features-hero-heading" data-id="element-331">
        <div className="max-w-7xl mx-auto" data-id="element-332">
          <div className="max-w-3xl" data-id="element-333">
            <Badge variant="amber" className="mb-6" data-id="element-334">
              Features
            </Badge>
            <h1 id="features-hero-heading" className="font-serif text-5xl md:text-6xl font-bold text-ink mb-6 leading-tight" data-id="element-335">
              The development layer your screenplay needs.
            </h1>
            <p className="font-sans text-lg text-ink-muted leading-relaxed max-w-2xl" data-id="element-336">
              Slate & Page isn't a script editor — it's where the thinking
              happens. It works alongside Final Draft, Arc Studio Pro, Highland,
              and any other tool you write in. We handle development; they
              handle the script.
            </p>
          </div>
        </div>
      </section>

      {/* Overview: How it fits */}
      <section className="bg-parchment py-16 px-6 lg:px-8" aria-labelledby="workflow-heading" data-id="element-337">
        <div className="max-w-7xl mx-auto" data-id="element-338">
          <div className="grid md:grid-cols-3 gap-8" data-id="element-339">
            <div className="text-center p-6" data-id="element-340">
              <div className="w-14 h-14 bg-white rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-sm border border-parchment" data-id="element-341">
                <LayersIcon className="w-7 h-7 text-amber" aria-hidden="true" data-id="element-342" />
              </div>
              <h3 className="font-serif text-lg font-semibold text-ink mb-2" data-id="element-343">
                Development Layer
              </h3>
              <p className="font-sans text-sm text-ink-muted leading-relaxed" data-id="element-344">
                The structured workspace where planning, continuity, and
                world-building live — before and during the writing of actual
                pages.
              </p>
            </div>
            <div className="text-center p-6" data-id="element-345">
              <div className="w-14 h-14 bg-white rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-sm border border-parchment" data-id="element-346">
                <LinkIcon className="w-7 h-7 text-amber" aria-hidden="true" data-id="element-347" />
              </div>
              <h3 className="font-serif text-lg font-semibold text-ink mb-2" data-id="element-348">
                Everything Connected
              </h3>
              <p className="font-sans text-sm text-ink-muted leading-relaxed" data-id="element-349">
                Characters link to scenes. Scenes link to locations. Locations
                link to world elements. Every piece of your story is
                interconnected.
              </p>
            </div>
            <div className="text-center p-6" data-id="element-350">
              <div className="w-14 h-14 bg-white rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-sm border border-parchment" data-id="element-351">
                <SearchIcon className="w-7 h-7 text-amber" aria-hidden="true" data-id="element-352" />
              </div>
              <h3 className="font-serif text-lg font-semibold text-ink mb-2" data-id="element-353">
                Searchable & Scannable
              </h3>
              <p className="font-sans text-sm text-ink-muted leading-relaxed" data-id="element-354">
                Find any detail instantly. No more digging through notebooks or
                scrolling through Google Docs at 2am.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Feature 1: Characters */}
      <section className="bg-cream py-24 px-6 lg:px-8" aria-labelledby="characters-heading" data-id="element-355">
        <div className="max-w-7xl mx-auto" data-id="element-356">
          <div className="grid lg:grid-cols-2 gap-16 items-start" data-id="element-357">
            <div data-id="element-358">
              <Badge variant="amber" className="mb-5" data-id="element-359">
                <UsersIcon className="w-3 h-3" aria-hidden="true" data-id="element-360" />
                Character Development
              </Badge>
              <h2 id="characters-heading" className="font-serif text-4xl font-bold text-ink mb-6 leading-tight" data-id="element-361">
                Rich profiles that capture who your characters really are.
              </h2>
              <p className="font-sans text-base text-ink-muted leading-relaxed mb-8" data-id="element-362">
                A character is more than a name and a description. Slate & Page
                gives you the space to capture everything that makes them real —
                their goals and fears, their speech patterns and coping styles,
                the formative events that shaped them — and then connects all of
                it to the scenes where it plays out.
              </p>

              <div className="space-y-6" data-id="element-363">
                <div className="border-l-2 border-amber pl-5" data-id="element-364">
                  <h3 className="font-serif text-lg font-semibold text-ink mb-1" data-id="element-365">
                    Emotional Arc Tracking
                  </h3>
                  <p className="font-sans text-sm text-ink-muted leading-relaxed" data-id="element-366">
                    See how Elena's emotional state shifts from Scene 3 to Scene
                    22. Track turning points, breakthroughs, and setbacks across
                    the full story.
                  </p>
                </div>
                <div className="border-l-2 border-amber/30 pl-5" data-id="element-367">
                  <h3 className="font-serif text-lg font-semibold text-ink mb-1" data-id="element-368">
                    Relationship Mapping
                  </h3>
                  <p className="font-sans text-sm text-ink-muted leading-relaxed" data-id="element-369">
                    Define how characters relate to each other — alliances,
                    conflicts, history, power dynamics — and see how those
                    relationships evolve.
                  </p>
                </div>
                <div className="border-l-2 border-amber/30 pl-5" data-id="element-370">
                  <h3 className="font-serif text-lg font-semibold text-ink mb-1" data-id="element-371">
                    Scene Presence
                  </h3>
                  <p className="font-sans text-sm text-ink-muted leading-relaxed" data-id="element-372">
                    Every character is linked to the scenes they appear in. See
                    their full presence across the story at a glance.
                  </p>
                </div>
                <div className="border-l-2 border-amber/30 pl-5" data-id="element-373">
                  <h3 className="font-serif text-lg font-semibold text-ink mb-1" data-id="element-374">
                    Voice & Speech Patterns
                  </h3>
                  <p className="font-sans text-sm text-ink-muted leading-relaxed" data-id="element-375">
                    Capture how each character speaks — their vocabulary,
                    rhythms, verbal tics — so their dialogue stays consistent
                    across the script.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-parchment rounded-2xl p-8 border border-parchment/80" data-id="element-376">
              <div className="bg-white rounded-xl shadow-sm border border-parchment overflow-hidden" data-id="element-377">
                <div className="bg-navy px-5 py-3 flex items-center justify-between" data-id="element-378">
                  <span className="font-sans text-xs text-white/60" data-id="element-379">
                    Character Profile
                  </span>
                  <span className="font-sans text-xs bg-amber/20 text-amber px-2.5 py-0.5 rounded-full" data-id="element-380">
                    Protagonist
                  </span>
                </div>
                <div className="p-5 space-y-4" data-id="element-381">
                  <div data-id="element-382">
                    <h3 className="font-serif text-xl font-bold text-ink" data-id="element-383">
                      Elena Marsh
                    </h3>
                    <p className="font-sans text-xs text-ink-light mt-0.5" data-id="element-384">
                      Feature Film · The Long Road
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-3" data-id="element-385">
                    <div className="bg-cream rounded-lg p-3 border border-parchment" data-id="element-386">
                      <div className="font-sans text-xs text-ink-light uppercase tracking-wide mb-1" data-id="element-387">
                        Core Goal
                      </div>
                      <p className="font-sans text-xs text-ink-muted" data-id="element-388">
                        Reconcile with estranged daughter
                      </p>
                    </div>
                    <div className="bg-cream rounded-lg p-3 border border-parchment" data-id="element-389">
                      <div className="font-sans text-xs text-ink-light uppercase tracking-wide mb-1" data-id="element-390">
                        Fatal Flaw
                      </div>
                      <p className="font-sans text-xs text-ink-muted" data-id="element-391">
                        Avoids vulnerability
                      </p>
                    </div>
                    <div className="bg-cream rounded-lg p-3 border border-parchment" data-id="element-392">
                      <div className="font-sans text-xs text-ink-light uppercase tracking-wide mb-1" data-id="element-393">
                        Arc Type
                      </div>
                      <p className="font-sans text-xs text-ink-muted" data-id="element-394">
                        Redemption
                      </p>
                    </div>
                    <div className="bg-cream rounded-lg p-3 border border-parchment" data-id="element-395">
                      <div className="font-sans text-xs text-ink-light uppercase tracking-wide mb-1" data-id="element-396">
                        Coping Style
                      </div>
                      <p className="font-sans text-xs text-ink-muted" data-id="element-397">
                        Overwork, deflection
                      </p>
                    </div>
                  </div>
                  <div className="bg-cream rounded-lg p-3 border border-parchment" data-id="element-398">
                    <div className="font-sans text-xs text-ink-light uppercase tracking-wide mb-2" data-id="element-399">
                      Emotional Arc
                    </div>
                    <div className="flex items-center gap-2" data-id="element-400">
                      <span className="font-sans text-xs text-ink-muted" data-id="element-401">
                        Sc. 1
                      </span>
                      <div className="flex-1 h-2 bg-parchment rounded-full overflow-hidden" data-id="element-402">
                        <div className="h-full bg-gradient-to-r from-red-300 via-amber/60 to-green-400 rounded-full" data-id="element-403"></div>
                      </div>
                      <span className="font-sans text-xs text-ink-muted" data-id="element-404">
                        Sc. 22
                      </span>
                    </div>
                    <div className="flex justify-between mt-1" data-id="element-405">
                      <span className="font-sans text-xs text-ink-light" data-id="element-406">
                        Closed off
                      </span>
                      <span className="font-sans text-xs text-ink-light" data-id="element-407">
                        Open
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Feature 2: Scene Planning */}
      <section className="bg-parchment py-24 px-6 lg:px-8" aria-labelledby="scenes-heading" data-id="element-408">
        <div className="max-w-7xl mx-auto" data-id="element-409">
          <div className="grid lg:grid-cols-2 gap-16 items-start" data-id="element-410">
            <div className="bg-white rounded-2xl p-8 border border-parchment shadow-sm order-2 lg:order-1" data-id="element-411">
              <div className="bg-navy rounded-xl overflow-hidden" data-id="element-412">
                <div className="px-5 py-3 border-b border-white/10" data-id="element-413">
                  <span className="font-sans text-xs text-white/60" data-id="element-414">
                    Scene 12 · The Confrontation
                  </span>
                </div>
                <div className="p-5 space-y-4" data-id="element-415">
                  <div data-id="element-416">
                    <div className="font-sans text-xs text-white/40 uppercase tracking-wide mb-1.5" data-id="element-417">
                      Logline
                    </div>
                    <p className="font-sans text-sm text-white/80 leading-relaxed italic" data-id="element-418">
                      "Elena finally confronts her daughter at the hospital, but
                      her inability to apologize turns the reunion into another
                      rupture."
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-3" data-id="element-419">
                    <div className="bg-white/5 rounded-lg p-3 border border-white/10" data-id="element-420">
                      <div className="font-sans text-xs text-white/40 uppercase tracking-wide mb-1" data-id="element-421">
                        Dramatic Turn
                      </div>
                      <p className="font-sans text-xs text-white/70" data-id="element-422">
                        Reversal
                      </p>
                    </div>
                    <div className="bg-white/5 rounded-lg p-3 border border-white/10" data-id="element-423">
                      <div className="font-sans text-xs text-white/40 uppercase tracking-wide mb-1" data-id="element-424">
                        Emotional State
                      </div>
                      <p className="font-sans text-xs text-white/70" data-id="element-425">
                        Desperate → Defeated
                      </p>
                    </div>
                  </div>
                  <div className="bg-white/5 rounded-lg p-3 border border-white/10" data-id="element-426">
                    <div className="font-sans text-xs text-white/40 uppercase tracking-wide mb-2" data-id="element-427">
                      Setup / Payoff
                    </div>
                    <div className="space-y-1.5" data-id="element-428">
                      <div className="flex items-center gap-2" data-id="element-429">
                        <span className="w-1.5 h-1.5 rounded-full bg-amber/60 flex-shrink-0" data-id="element-430"></span>
                        <span className="font-sans text-xs text-white/70" data-id="element-431">
                          Sets up: Elena's final decision in Sc. 19
                        </span>
                      </div>
                      <div className="flex items-center gap-2" data-id="element-432">
                        <span className="w-1.5 h-1.5 rounded-full bg-amber/60 flex-shrink-0" data-id="element-433"></span>
                        <span className="font-sans text-xs text-white/70" data-id="element-434">
                          Pays off: The letter from Sc. 4
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="order-1 lg:order-2" data-id="element-435">
              <Badge variant="amber" className="mb-5" data-id="element-436">
                <BookOpenIcon className="w-3 h-3" aria-hidden="true" data-id="element-437" />
                Scene Planning
              </Badge>
              <h2 id="scenes-heading" className="font-serif text-4xl font-bold text-ink mb-6 leading-tight" data-id="element-438">
                See the shape of your story, scene by scene.
              </h2>
              <p className="font-sans text-base text-ink-muted leading-relaxed mb-8" data-id="element-439">
                Scene planning in Slate & Page goes beyond a simple outline.
                Each scene is a structured entry with loglines, beats, dramatic
                turns, and emotional arcs — so you can see not just what
                happens, but why it matters.
              </p>

              <div className="space-y-6" data-id="element-440">
                <div className="border-l-2 border-amber pl-5" data-id="element-441">
                  <h3 className="font-serif text-lg font-semibold text-ink mb-1" data-id="element-442">
                    Dramatic Structure
                  </h3>
                  <p className="font-sans text-sm text-ink-muted leading-relaxed" data-id="element-443">
                    Tag each scene with its dramatic function: setup,
                    escalation, reversal, revelation, climax. See the structure
                    of your story at a glance.
                  </p>
                </div>
                <div className="border-l-2 border-amber/30 pl-5" data-id="element-444">
                  <h3 className="font-serif text-lg font-semibold text-ink mb-1" data-id="element-445">
                    Setup & Payoff Tracking
                  </h3>
                  <p className="font-sans text-sm text-ink-muted leading-relaxed" data-id="element-446">
                    Link setups to their payoffs across the script. Never lose
                    track of a planted detail or a promised moment.
                  </p>
                </div>
                <div className="border-l-2 border-amber/30 pl-5" data-id="element-447">
                  <h3 className="font-serif text-lg font-semibold text-ink mb-1" data-id="element-448">
                    Emotional Arc per Scene
                  </h3>
                  <p className="font-sans text-sm text-ink-muted leading-relaxed" data-id="element-449">
                    Track the emotional state of each character entering and
                    leaving every scene. Spot flat stretches and missing beats.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Feature 3: Locations */}
      <section className="bg-cream py-24 px-6 lg:px-8" aria-labelledby="locations-heading" data-id="element-450">
        <div className="max-w-7xl mx-auto" data-id="element-451">
          <div className="grid lg:grid-cols-2 gap-16 items-start" data-id="element-452">
            <div data-id="element-453">
              <Badge variant="amber" className="mb-5" data-id="element-454">
                <MapPinIcon className="w-3 h-3" aria-hidden="true" data-id="element-455" />
                Location Management
              </Badge>
              <h2 id="locations-heading" className="font-serif text-4xl font-bold text-ink mb-6 leading-tight" data-id="element-456">
                Every space has a story. Track it.
              </h2>
              <p className="font-sans text-base text-ink-muted leading-relaxed mb-8" data-id="element-457">
                Locations in Slate & Page aren't just addresses — they're
                narrative elements with mood, atmosphere, visual identity, and
                transformation arcs. Track how your spaces change from the first
                scene to the last.
              </p>

              <div className="space-y-6" data-id="element-458">
                <div className="border-l-2 border-amber pl-5" data-id="element-459">
                  <h3 className="font-serif text-lg font-semibold text-ink mb-1" data-id="element-460">
                    Mood & Atmosphere
                  </h3>
                  <p className="font-sans text-sm text-ink-muted leading-relaxed" data-id="element-461">
                    Capture the emotional register of each space. Lighting,
                    sound, texture, temperature — the sensory details that make
                    a location feel real.
                  </p>
                </div>
                <div className="border-l-2 border-amber/30 pl-5" data-id="element-462">
                  <h3 className="font-serif text-lg font-semibold text-ink mb-1" data-id="element-463">
                    Visual Identity
                  </h3>
                  <p className="font-sans text-sm text-ink-muted leading-relaxed" data-id="element-464">
                    Define the color palette, visual motifs, and production
                    design notes for each location. Keep your cinematographer
                    and production designer aligned.
                  </p>
                </div>
                <div className="border-l-2 border-amber/30 pl-5" data-id="element-465">
                  <h3 className="font-serif text-lg font-semibold text-ink mb-1" data-id="element-466">
                    Transformation Arc
                  </h3>
                  <p className="font-sans text-sm text-ink-muted leading-relaxed" data-id="element-467">
                    How does the Marsh House feel in Act I versus Act III? Track
                    how your locations change meaning as the story progresses.
                  </p>
                </div>
                <div className="border-l-2 border-amber/30 pl-5" data-id="element-468">
                  <h3 className="font-serif text-lg font-semibold text-ink mb-1" data-id="element-469">
                    Production Notes
                  </h3>
                  <p className="font-sans text-sm text-ink-muted leading-relaxed" data-id="element-470">
                    Practical details for production: access, permits, special
                    requirements. Useful for indie filmmakers wearing multiple
                    hats.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-parchment rounded-2xl p-8 border border-parchment/80" data-id="element-471">
              <div className="bg-white rounded-xl shadow-sm border border-parchment overflow-hidden" data-id="element-472">
                <div className="bg-navy px-5 py-3" data-id="element-473">
                  <span className="font-sans text-xs text-white/60" data-id="element-474">
                    Location · The Marsh House
                  </span>
                </div>
                <div className="p-5 space-y-4" data-id="element-475">
                  <div className="flex flex-wrap gap-2" data-id="element-476">
                    <span className="bg-amber-light text-warm-brown text-xs font-sans px-2.5 py-1 rounded-full border border-amber/20" data-id="element-477">
                      Isolated
                    </span>
                    <span className="bg-amber-light text-warm-brown text-xs font-sans px-2.5 py-1 rounded-full border border-amber/20" data-id="element-478">
                      Decaying
                    </span>
                    <span className="bg-amber-light text-warm-brown text-xs font-sans px-2.5 py-1 rounded-full border border-amber/20" data-id="element-479">
                      Nostalgic
                    </span>
                    <span className="bg-amber-light text-warm-brown text-xs font-sans px-2.5 py-1 rounded-full border border-amber/20" data-id="element-480">
                      Liminal
                    </span>
                  </div>
                  <div className="bg-cream rounded-lg p-3 border border-parchment" data-id="element-481">
                    <div className="font-sans text-xs text-ink-light uppercase tracking-wide mb-2" data-id="element-482">
                      Color Palette
                    </div>
                    <div className="flex gap-2 items-center" data-id="element-483">
                      <div className="w-8 h-8 rounded-lg bg-amber/50 border border-parchment" data-id="element-484"></div>
                      <div className="w-8 h-8 rounded-lg bg-slate-500 border border-parchment" data-id="element-485"></div>
                      <div className="w-8 h-8 rounded-lg bg-stone-300 border border-parchment" data-id="element-486"></div>
                      <div className="w-8 h-8 rounded-lg bg-green-800 border border-parchment" data-id="element-487"></div>
                      <span className="font-sans text-xs text-ink-light ml-1" data-id="element-488">
                        Amber, Slate, Stone, Forest
                      </span>
                    </div>
                  </div>
                  <div className="bg-cream rounded-lg p-3 border border-parchment" data-id="element-489">
                    <div className="font-sans text-xs text-ink-light uppercase tracking-wide mb-1.5" data-id="element-490">
                      Transformation Arc
                    </div>
                    <p className="font-sans text-xs text-ink-muted leading-relaxed" data-id="element-491">
                      Act I: Dread, avoidance → Act II: Confrontation → Act III:
                      Reconciliation, warmth
                    </p>
                  </div>
                  <div className="bg-cream rounded-lg p-3 border border-parchment" data-id="element-492">
                    <div className="font-sans text-xs text-ink-light uppercase tracking-wide mb-2" data-id="element-493">
                      Scenes Here
                    </div>
                    <div className="flex flex-wrap gap-1.5" data-id="element-494">
                      <span className="bg-navy/10 text-navy text-xs font-sans px-2 py-0.5 rounded-full" data-id="element-495">
                        Sc. 1
                      </span>
                      <span className="bg-navy/10 text-navy text-xs font-sans px-2 py-0.5 rounded-full" data-id="element-496">
                        Sc. 7
                      </span>
                      <span className="bg-amber-light text-warm-brown text-xs font-sans px-2 py-0.5 rounded-full border border-amber/20" data-id="element-497">
                        Sc. 12 ★
                      </span>
                      <span className="bg-navy/10 text-navy text-xs font-sans px-2 py-0.5 rounded-full" data-id="element-498">
                        Sc. 20
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Feature 4: World-Building */}
      <section className="bg-parchment py-24 px-6 lg:px-8" aria-labelledby="worldbuilding-heading" data-id="element-499">
        <div className="max-w-7xl mx-auto" data-id="element-500">
          <div className="grid lg:grid-cols-2 gap-16 items-start" data-id="element-501">
            <div className="bg-white rounded-2xl p-8 border border-parchment shadow-sm order-2 lg:order-1" data-id="element-502">
              <div className="space-y-3" data-id="element-503">
                <div className="flex items-center justify-between p-4 bg-cream rounded-xl border border-parchment" data-id="element-504">
                  <div className="flex items-center gap-3" data-id="element-505">
                    <div className="w-9 h-9 bg-amber-light rounded-lg flex items-center justify-center" data-id="element-506">
                      <span className="font-sans text-sm" data-id="element-507">⚖️</span>
                    </div>
                    <div data-id="element-508">
                      <div className="font-sans text-sm font-medium text-ink" data-id="element-509">
                        The Accord
                      </div>
                      <div className="font-sans text-xs text-ink-light" data-id="element-510">
                        Rule · Referenced in 6 scenes
                      </div>
                    </div>
                  </div>
                  <span className="font-sans text-xs bg-amber-light text-warm-brown px-2 py-0.5 rounded-full border border-amber/20" data-id="element-511">
                    Rule
                  </span>
                </div>
                <div className="flex items-center justify-between p-4 bg-cream rounded-xl border border-parchment" data-id="element-512">
                  <div className="flex items-center gap-3" data-id="element-513">
                    <div className="w-9 h-9 bg-navy/10 rounded-lg flex items-center justify-center" data-id="element-514">
                      <span className="font-sans text-sm" data-id="element-515">📖</span>
                    </div>
                    <div data-id="element-516">
                      <div className="font-sans text-sm font-medium text-ink" data-id="element-517">
                        The Ledger
                      </div>
                      <div className="font-sans text-xs text-ink-light" data-id="element-518">
                        Object · Carried by Elena
                      </div>
                    </div>
                  </div>
                  <span className="font-sans text-xs bg-navy/10 text-navy px-2 py-0.5 rounded-full" data-id="element-519">
                    Object
                  </span>
                </div>
                <div className="flex items-center justify-between p-4 bg-cream rounded-xl border border-parchment" data-id="element-520">
                  <div className="flex items-center gap-3" data-id="element-521">
                    <div className="w-9 h-9 bg-purple-100 rounded-lg flex items-center justify-center" data-id="element-522">
                      <span className="font-sans text-sm" data-id="element-523">🏛️</span>
                    </div>
                    <div data-id="element-524">
                      <div className="font-sans text-sm font-medium text-ink" data-id="element-525">
                        The Council
                      </div>
                      <div className="font-sans text-xs text-ink-light" data-id="element-526">
                        Faction · Antagonist organization
                      </div>
                    </div>
                  </div>
                  <span className="font-sans text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full" data-id="element-527">
                    Faction
                  </span>
                </div>
                <div className="flex items-center justify-between p-4 bg-cream rounded-xl border border-parchment" data-id="element-528">
                  <div className="flex items-center gap-3" data-id="element-529">
                    <div className="w-9 h-9 bg-green-100 rounded-lg flex items-center justify-center" data-id="element-530">
                      <span className="font-sans text-sm" data-id="element-531">💬</span>
                    </div>
                    <div data-id="element-532">
                      <div className="font-sans text-sm font-medium text-ink" data-id="element-533">
                        Greywalker
                      </div>
                      <div className="font-sans text-xs text-ink-light" data-id="element-534">
                        Terminology · Used by Elena
                      </div>
                    </div>
                  </div>
                  <span className="font-sans text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full" data-id="element-535">
                    Term
                  </span>
                </div>
              </div>
            </div>

            <div className="order-1 lg:order-2" data-id="element-536">
              <Badge variant="amber" className="mb-5" data-id="element-537">
                <GlobeIcon className="w-3 h-3" aria-hidden="true" data-id="element-538" />
                World-Building
              </Badge>
              <h2 id="worldbuilding-heading" className="font-serif text-4xl font-bold text-ink mb-6 leading-tight" data-id="element-539">
                Build the world. Keep it consistent.
              </h2>
              <p className="font-sans text-base text-ink-muted leading-relaxed mb-8" data-id="element-540">
                From the rules that govern your world to the slang your
                characters use, Slate & Page gives you a modular system for
                every element of your story's universe — all cross-referenced
                with the characters and scenes that bring it to life.
              </p>

              <div className="grid grid-cols-2 gap-4 mb-8" data-id="element-541">
                <div className="bg-white rounded-xl p-4 border border-parchment" data-id="element-542">
                  <div className="font-serif text-2xl font-bold text-amber mb-1" data-id="element-543">
                    Rules
                  </div>
                  <p className="font-sans text-xs text-ink-muted" data-id="element-544">
                    Magic systems, laws, physics — the logic that governs your
                    world
                  </p>
                </div>
                <div className="bg-white rounded-xl p-4 border border-parchment" data-id="element-545">
                  <div className="font-serif text-2xl font-bold text-amber mb-1" data-id="element-546">
                    Objects
                  </div>
                  <p className="font-sans text-xs text-ink-muted" data-id="element-547">
                    MacGuffins, artifacts, character identifiers, symbolic items
                  </p>
                </div>
                <div className="bg-white rounded-xl p-4 border border-parchment" data-id="element-548">
                  <div className="font-serif text-2xl font-bold text-amber mb-1" data-id="element-549">
                    Factions
                  </div>
                  <p className="font-sans text-xs text-ink-muted" data-id="element-550">
                    Organizations, families, gangs, institutions, and their
                    dynamics
                  </p>
                </div>
                <div className="bg-white rounded-xl p-4 border border-parchment" data-id="element-551">
                  <div className="font-serif text-2xl font-bold text-amber mb-1" data-id="element-552">
                    Terms
                  </div>
                  <p className="font-sans text-xs text-ink-muted" data-id="element-553">
                    Invented vocabulary, slang, jargon, and world-specific
                    language
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Workflow section */}
      <section className="bg-navy py-24 px-6 lg:px-8" aria-labelledby="workflow-fit-heading" data-id="element-554">
        <div className="max-w-7xl mx-auto" data-id="element-555">
          <div className="text-center max-w-2xl mx-auto mb-16" data-id="element-556">
            <h2 id="workflow-fit-heading" className="font-serif text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-557">
              It fits your workflow. Not the other way around.
            </h2>
            <p className="font-sans text-base text-white/60 leading-relaxed" data-id="element-558">
              Slate & Page works alongside the tools you already use. Keep
              writing in Final Draft, Arc Studio Pro, or Highland — and use
              Slate & Page for everything that happens around the script.
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-6" data-id="element-559">
            <div className="bg-white/5 border border-white/10 rounded-2xl p-6 text-center" data-id="element-560">
              <div className="font-sans text-2xl mb-3" data-id="element-561">📝</div>
              <div className="font-serif text-base font-semibold text-white mb-1" data-id="element-562">
                Final Draft
              </div>
              <div className="font-sans text-xs text-white/50" data-id="element-563">
                Script writing
              </div>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-2xl p-6 text-center" data-id="element-564">
              <div className="font-sans text-2xl mb-3" data-id="element-565">🎬</div>
              <div className="font-serif text-base font-semibold text-white mb-1" data-id="element-566">
                Arc Studio Pro
              </div>
              <div className="font-sans text-xs text-white/50" data-id="element-567">
                Script writing
              </div>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-2xl p-6 text-center" data-id="element-568">
              <div className="font-sans text-2xl mb-3" data-id="element-569">⛰️</div>
              <div className="font-serif text-base font-semibold text-white mb-1" data-id="element-570">
                Highland 2
              </div>
              <div className="font-sans text-xs text-white/50" data-id="element-571">
                Script writing
              </div>
            </div>
            <div className="bg-amber/20 border border-amber/30 rounded-2xl p-6 text-center" data-id="element-572">
              <div className="font-sans text-2xl mb-3" data-id="element-573">🎞️</div>
              <div className="font-serif text-base font-semibold text-amber mb-1" data-id="element-574">
                Slate & Page
              </div>
              <div className="font-sans text-xs text-amber/70" data-id="element-575">
                Development layer
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-cream py-24 px-6 lg:px-8" aria-labelledby="features-cta-heading" data-id="element-576">
        <div className="max-w-3xl mx-auto text-center" data-id="element-577">
          <h2 id="features-cta-heading" className="font-serif text-4xl font-bold text-ink mb-6" data-id="element-578">
            Ready to see it in action?
          </h2>
          <p className="font-sans text-lg text-ink-muted mb-10" data-id="element-579">
            Start with the free plan and build your first story bible today.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-580">
            <a href="#" className="font-sans font-medium text-base bg-amber hover:bg-amber-dark text-white px-10 py-4 rounded-full transition-colors shadow-md" data-id="element-581">
              Start Free
            </a>
            <Link to="/pricing" className="font-sans font-medium text-base text-ink-muted hover:text-ink px-10 py-4 rounded-full border border-parchment hover:border-ink-light transition-colors" data-id="element-582">
              View Pricing
            </Link>
          </div>
        </div>
      </section>
    </main>;
}